import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { Heart, MessageCircle, Send } from 'lucide-react'
import { cn } from '@/lib/utils'

interface PhoneFrameProps {
    image?: string | null
    alt?: string | null
    video?: string | null
    autoPlay?: boolean
    playOnHover?: boolean
    className?: string
    children?: ReactNode
}

const PhoneFrame: FC<PhoneFrameProps> = ({
    image,
    alt,
    video,
    autoPlay = false,
    playOnHover = false,
    className,
    children
}): ReactNode => {
    const videoRef = useRef<HTMLVideoElement>(null)

    const play = () => {
        if (!playOnHover || !videoRef.current) return
        videoRef.current.play().catch(() => undefined)
    }

    const pause = () => {
        if (!playOnHover || !videoRef.current) return
        videoRef.current.pause()
    }

    return (
        <div
            onMouseEnter={play}
            onMouseLeave={pause}
            className={cn(
                'relative aspect-[9/16] w-full overflow-hidden rounded-[1.75rem] border-[6px] border-ink bg-ink shadow-[0_30px_60px_-25px_rgba(20,20,20,0.55)]',
                className
            )}
        >
            {video ? (
                <video
                    ref={videoRef}
                    src={video}
                    poster={image ?? undefined}
                    muted
                    loop
                    playsInline
                    autoPlay={autoPlay}
                    preload={autoPlay ? 'auto' : 'metadata'}
                    className='absolute inset-0 size-full object-cover'
                    aria-label={alt ?? undefined}
                />
            ) : image ? (
                <img
                    src={image}
                    alt={alt ?? ''}
                    loading='lazy'
                    className='absolute inset-0 size-full object-cover'
                />
            ) : (
                <div className='absolute inset-0 bg-gradient-to-br from-ink via-ink/90 to-signal/60' />
            )}

            <div className='pointer-events-none absolute inset-0 bg-gradient-to-b from-ink/35 via-transparent to-ink/80' aria-hidden='true' />

            <div className='pointer-events-none absolute top-3 left-1/2 h-1.5 w-12 -translate-x-1/2 rounded-full bg-ink/80' aria-hidden='true' />

            <div className='pointer-events-none absolute right-2.5 bottom-20 flex flex-col items-center gap-3.5 text-bone' aria-hidden='true'>
                <Heart className='size-5 drop-shadow' />
                <MessageCircle className='size-5 drop-shadow' />
                <Send className='size-5 drop-shadow' />
            </div>

            {children && (
                <div className='absolute inset-x-0 bottom-0 p-3.5 pr-10 text-bone sm:p-4 sm:pr-11'>
                    {children}
                </div>
            )}
        </div>
    )
}

export default PhoneFrame
